import { PackageStatus } from '@qdrant/qdrant-cloud-public-api/booking/v1';
import { ClusterService } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { PaymentService } from '@qdrant/qdrant-cloud-public-api/payment/v1';
import { SpaceService } from '@qdrant/qdrant-cloud-public-api/serverless/space/v1';
import { createFileRoute, lazyRouteComponent, redirect, type StaticDataRouteOption } from '@tanstack/react-router';
import { assertRpcPermissions, checkRpcPermissions } from '@/api/permissions';
import { BookingServices } from '@/api/services/booking';
import { clusterService } from '@/api/services/cluster';
import { paymentService } from '@/api/services/payment';
import { platformService } from '@/api/services/platform';
import { quotaService } from '@/api/services/quota';
import { serverlessPlatformService } from '@/api/services/serverless/platform';
import { spaceService } from '@/api/services/serverless/space';
import { isFreeTemplate } from '@/components/Clusters/ClusterCreation/utils/isFreeTemplate';
import { freeTierDefaultCloudProviderExperiment } from '@/helpers/statsig/experiment';
import { checkServerlessFeatureGate } from '@/helpers/statsig/featureGate';
import { ensureStatsigUserFeatureGateReady } from '@/helpers/statsig/featureGate/ensureStatsigUserFeatureGateReady';
import { enqueueSnackbar } from '@/hooks/use-qdrant-snackbar';
import { head } from '@/router/Head';
import {
  createTemplateFilterByExperiment,
  resolveClusterCreationEnabled,
  resolvePreloadedProvidersRegions,
  resolveValidFreeTierProvider,
  resolveValidProviderRegionPair,
} from '@/router/resolvers/cluster';
import { maxClusterQuotaResolver } from '@/router/resolvers/quota';
import { resolveClusterRouteEntrypoint } from '@/router/resolvers/user';
import { clusterCreateSearchSchema, clusterSetupSearchSchema } from '@/router/utils';

/**
 * A space is not a cluster: `?template=serverless` names the tab after it, as the page heading does.
 * Typed down to what it reads, like `head` itself, so the route's own types do not recurse through it.
 */
function clusterCreateHead(ctx: {
  matches: { staticData: StaticDataRouteOption }[];
  match: { search: { template?: string } };
}) {
  if (ctx.match.search.template !== 'serverless') {
    return head(ctx);
  }

  // This route is the leaf, so its title is the last one `head` joins.
  return head({ matches: [...ctx.matches.slice(0, -1), { staticData: { title: 'Create Serverless Space' } }] });
}

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/clusters/create')({
  validateSearch: clusterCreateSearchSchema.and(clusterSetupSearchSchema),
  staticData: {
    title: 'Create Cluster',
  },
  loaderDeps: ({ search: { provider, region, template } }) => ({ provider, region, template }),
  beforeLoad: async ({ context, params, search }) => {
    const { accountId } = params;
    const { queryClient, statsigClient } = context;

    const listCloudProvidersQuery = platformService.listCloudProvidersQuery({ accountId });

    const canReadPaymentInformation = checkRpcPermissions([PaymentService.method.listPaymentMethods], {
      queryClient,
      accountId,
    }).matches;

    // One context shape for every template, so create.hooks has a single context type.
    // The loader fetches only what the selected template renders.
    const createContext = (provider: string) => ({
      suggestClusterNameQuery: clusterService.suggestClusterNameQuery({ accountId }),
      listPackagesQuery: BookingServices.listPackagesQuery({
        accountId,
        cloudProviderId: search.provider ?? '',
        cloudProviderRegionId: search.region,
        statuses: [PackageStatus.ACTIVE, PackageStatus.DEACTIVATED],
      }),
      listCloudProvidersQuery,
      listCloudProviderRegionsQuery: platformService.listCloudProviderRegionsQuery({
        accountId,
        cloudProviderId: provider,
      }),
      listPaymentMethodsQuery:
        !isFreeTemplate(search.template) && canReadPaymentInformation
          ? paymentService.listPaymentMethodsQuery({ accountId })
          : null,
      listSpacesQuery: spaceService.listSpacesQuery({ accountId }),
      suggestSpaceNameQuery: spaceService.suggestSpaceNameQuery({ accountId }),
      listServerlessRegionsQuery: serverlessPlatformService.listCloudRegionsQuery({ accountId }),
    });

    await ensureStatsigUserFeatureGateReady(context);

    // A space is not a cluster: none of the cluster gates, quotas or provider checks below apply.
    if (search.template === 'serverless') {
      if (!checkServerlessFeatureGate(statsigClient)) {
        throw redirect({ to: '.', search: { ...search, template: 'standard' }, replace: true });
      }

      // The existing spaces validate the new name, so reading them is as required as creating one.
      assertRpcPermissions([SpaceService.method.createSpace, SpaceService.method.listSpaces], {
        queryClient,
        accountId,
      });

      return createContext(search.provider ?? '');
    }

    // The page only reads until submit, so gate it on what `CreateCluster` itself requires — a
    // read-only user gets the Access Denied screen instead of a form that could never be submitted.
    // Permissions come from cache: `$accountId` awaits them in its own beforeLoad, which resolves first.
    assertRpcPermissions([ClusterService.method.createCluster], { queryClient, accountId });

    const clustersByAccountIdQuery = clusterService.listClustersQuery({ accountId });
    const getAccountQuotasQuery = quotaService.getAccountQuotasQuery({ accountId });

    resolveClusterCreationEnabled(statsigClient, params);

    const isClusterQuotaSufficient = await maxClusterQuotaResolver({
      queryClient,
      clustersByAccountIdQuery,
      getAccountQuotasQuery,
    });
    // Ensure the account has enough quota to create a new cluster
    if (!isClusterQuotaSufficient) {
      enqueueSnackbar({
        message: (
          <>
            You have reached the maximum number of clusters allowed for your account.
            <br />
            Please contact Qdrant Support if you wish to increase your cluster limit.
          </>
        ),
        variant: 'warning',
      });
      await resolveClusterRouteEntrypoint({ accountId, ...context });
    }

    const { cloud_provider: experimentCloudProvider } = freeTierDefaultCloudProviderExperiment(statsigClient);
    // Ensure provider, region and template fulfill business heuristics
    await resolveValidFreeTierProvider({
      queryClient,
      listCloudProvidersQuery,
      search,
      experimentCloudProvider,
    });
    const { provider } = await resolveValidProviderRegionPair({
      queryClient,
      search,
      accountId,
      listCloudProvidersQuery,
      filter: createTemplateFilterByExperiment(search.template, experimentCloudProvider),
    });

    return createContext(provider);
  },
  loader: async ({
    context: {
      queryClient,
      listPackagesQuery,
      suggestClusterNameQuery,
      listPaymentMethodsQuery,
      listCloudProvidersQuery,
      listSpacesQuery,
      suggestSpaceNameQuery,
      listServerlessRegionsQuery,
    },
    params: { accountId },
    deps: { template },
  }) => {
    const requests: Promise<unknown>[] = [];

    if (listPaymentMethodsQuery) {
      requests.push(queryClient.fetchQuery(listPaymentMethodsQuery));
    }

    if (template === 'serverless') {
      void queryClient.prefetchQuery(suggestSpaceNameQuery);

      // The cloud providers decide whether the free cluster card is offered next to the space.
      requests.push(
        queryClient.fetchQuery(listSpacesQuery),
        queryClient.fetchQuery(listServerlessRegionsQuery),
        queryClient.fetchQuery(listCloudProvidersQuery),
      );

      return Promise.all(requests);
    }

    // No hybrid-cloud gate here: `ListHybridCloudEnvironments` takes `read:hybrid_cloud_environments`
    // *or* `write:clusters`, and `beforeLoad` already refused everyone without the latter.
    await resolvePreloadedProvidersRegions({ queryClient, accountId });

    requests.push(queryClient.fetchQuery(listPackagesQuery));

    void queryClient.prefetchQuery(suggestClusterNameQuery);

    return Promise.all(requests);
  },
  head: clusterCreateHead,
  component: lazyRouteComponent(() => import('./create.component'), 'ClusterCreateComponent'),
  pendingComponent: lazyRouteComponent(() => import('./create.component'), 'ClusterCreatePendingComponent'),
  notFoundComponent: lazyRouteComponent(() => import('./create.component'), 'ClusterCreateNotFoundFallback'),
});
